import React, {useEffect, useMemo, useState} from 'react';
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    FormGroup,
    FormHelperText,
    InputAdornment,
    MenuItem,
    Radio,
    RadioGroup,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import PropTypes from 'prop-types';
import useTextFields from '../utility/hooks/useTextFields';
import {yellow} from '@mui/material/colors';
import WarningIcon from '@mui/icons-material/Warning';
import {DatePicker} from '@mui/x-date-pickers';
import ListItemText from '@mui/material/ListItemText';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {HOVER_BOX} from './InfoHover';

const OptionComponent = ({type, ...props}) => ({
    checkbox: () => <Checkbox {...props}/>,
    switch: () => <Switch {...props}/>
})[type]() ?? '';

const AdmicityForm = (
    {
        title,
        textFields,
        options,
        custom,
        handleSubmit,
        handleChange,
        errorCaption,
        buttonText,
        actionsContainerProps,
        buttonFullWidth = true,
        isButtonDisabled,
        setIsChildFormValid = null,
        showWarningMessage = false,
        fieldsGrid = null
    }, ref) => {
    const {fields, reset} = useTextFields(textFields);
    const [selectedOptions, setSelectedOptions] = useState([]);
    const [customFields, setCustomFields] = useState({});
    const [isSubmitted, setIsSubmitted] = useState(false);
    /* eslint-disable react/prop-types */
    const isFormValid = useMemo(() => {
        return !Object.values(fields).some(({textFieldProps: props}) => props.error || (props.value === '' && props.required));
    }, [fields]);

    const textFieldBackgroundStyle = {
        '& .MuiOutlinedInput-root': { backgroundColor: '#fff' },
        '& .MuiOutlinedInput-notchedOutline': {backgroundColor: 'transparent'},
        '& .MuiOutlinedInput-input, .MuiOutlinedInput-inputMultiline, .MuiSelect-select': {color: '#0A0A0A'},
    };

    /* eslint-enable react/prop-types */
    const fieldComponents = {
        select: (textFieldProps) =>
            (
                <TextField
                    key={`select_${textFieldProps.name}`}
                    variant="outlined"
                    select
                    margin="dense"
                    disabled={isSubmitted}
                    fullWidth
                    sx={textFieldBackgroundStyle}
                    {...textFieldProps}
                >
                    {textFields.length
                        ? textFields.find(x => x.name === textFieldProps.name)
                            ?.options
                            ?.map(x =>
                                textFieldProps.SelectProps?.multiple
                                    ? <MenuItem key={x.value} value={x.value}>
                                        <Checkbox checked={textFieldProps.value.includes(x.value)}/>
                                        <ListItemText primary={x.title}/>
                                    </MenuItem>
                                    : <MenuItem key={x.value} value={x.value}>{x.title}</MenuItem>
                            )
                        : <MenuItem/>
                    }
                </TextField>
            ),
        date: (textFieldProps) => (
            <>
            <DatePicker
                format={'D-MMM-YYYY'}
                slotProps={{
                    textField: {
                        fullWidth: true,
                        name: textFieldProps.name,
                        margin: 'dense',
                        sx: textFieldBackgroundStyle,
                    },
                    field: {
                        readOnly: true
                    }
                }}
                {...{
                    ...textFieldProps,
                    onChange: value => textFieldProps.onChange({target: {value, name: textFieldProps.name}}, {})
                }}
            />
                {textFieldProps.error && <FormHelperText error>
                    {textFieldProps.helperText}
                </FormHelperText>
                }
            </>
        ),
        text: (textFieldProps) => (
            <React.Fragment key={textFieldProps.name}>
                {showWarningMessage && textFieldProps.changed && textFieldProps.onChangeWarningMessage && (
                    <Box display="flex" alignItems="center" mt={1}>
                        <WarningIcon sx={{color: yellow[700], mr: 1}}/>
                        <FormHelperText sx={{fontWeight: 'bold'}} error>
                            {textFieldProps.onChangeWarningMessage}
                        </FormHelperText>
                    </Box>
                )}
                <TextField
                    key={`textField_${textFieldProps.name}`}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    disabled={isSubmitted}
                    sx={textFieldBackgroundStyle}
                    {...textFieldProps}
                />
            </React.Fragment>
        ),
        radio: (textFieldProps) => {
            const options = textFields.find(x => x.name === textFieldProps.name)?.options || [];
            return (
                <>
                    <Typography mt={1}>{textFieldProps.label}</Typography>
                    <RadioGroup
                        name={textFieldProps.name}
                        value={textFieldProps.value}
                        onChange={textFieldProps.onChange}
                    >
                        {options.map(option => (
                            <FormControlLabel
                                key={option.value}
                                value={option.value}
                                control={<Radio size={'small'} />}
                                label={option.title}
                            />
                        ))}
                    </RadioGroup>
                    {textFieldProps.error && <FormHelperText>{textFieldProps.helperText}</FormHelperText>}
                </>
            );
        }
    };

    useEffect(() => {
        return () => {
            reset();
            setSelectedOptions([]);
        };
    }, []);

    useEffect(() => {
        let activeOptionsIndexes = options?.reduce((result, option, index) => {
            if (option.initialValue) {
                result.push(index);
            }
            return result;
        }, []);

        setSelectedOptions(activeOptionsIndexes);
    }, [options]);

    useEffect(() => {
        setCustomFields(custom?.reduce((fields, customField) => {
            fields[customField.name] = customField.initialValue;
            return fields;
        }, {}));
    }, [custom]);

    useEffect(() => {
        if (setIsChildFormValid !== null) {
            setIsChildFormValid(isFormValid);
        }
    }, [isFormValid]);

    const onSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitted(true);
        const textFieldValues = Object.values(fields).reduce((obj, field) => {
            obj[field.textFieldProps.name] = field.otherProps.nullableOnEmpty && field.textFieldProps.value === ''
                ? null
                : field.textFieldProps.value;
            return obj;
        }, {});
        const optionValues = options?.reduce((obj, option, index) => {
            obj[option.name] = selectedOptions.includes(index);
            return obj;
        }, {});

        try {
            await handleSubmit({...textFieldValues, ...optionValues, ...customFields});
        } catch (e) {
        } finally {
            setIsSubmitted(false);
        }
    };

    const handleSelectOption = (event, index) => {
        if (!event.target.checked) {
            setSelectedOptions(selectedOptions.filter(x => x !== index));
        } else {
            setSelectedOptions([...selectedOptions, index]);
        }
    };

    return (
        <form ref={ref} onChange={handleChange} onSubmit={onSubmit}>
            <Typography
                variant="h6"
                textAlign="center"
            >{title}
            </Typography>
            {fieldsGrid
                // the fields in rows across the page: fieldsGrid.sx sets the columns, fieldsGrid.span each field's width,
                // fieldsGrid.headings a heading across the row before a field, fieldsGrid.hovers the words shown on hover
                ? (
                    <Box sx={{display: 'grid', columnGap: 2, ...fieldsGrid.sx}}>
                        {Object.values(fields).map(({textFieldProps}) => {
                            const hover = fieldsGrid.hovers?.[textFieldProps.name];
                            // Maria, 20 October 2026: an 'i' icon in the box, with the explanation shown on hover
                            const field = fieldComponents[textFieldProps.inputType ?? 'text']({
                                ...textFieldProps,
                                ...(fieldsGrid.size ? {size: fieldsGrid.size} : {}),
                                // fieldsGrid.labelHovers: an 'i' icon straight after the field's name, with the words on hover
                                ...(fieldsGrid.labelHovers?.[textFieldProps.name]
                                    ? {
                                        label: (
                                            <Box component="span" sx={{display: 'inline-flex', alignItems: 'center', gap: 0.5}}>
                                                {textFieldProps.label}
                                                <Tooltip title={fieldsGrid.labelHovers[textFieldProps.name]} arrow placement="top" componentsProps={HOVER_BOX}>
                                                    <InfoOutlinedIcon
                                                        fontSize="small"
                                                        sx={{color: '#0A8572', cursor: 'help'}}
                                                        data-testid={`hover-${textFieldProps.name}`}
                                                    />
                                                </Tooltip>
                                            </Box>
                                        ),
                                        InputLabelProps: {sx: {pointerEvents: 'auto'}}
                                    }
                                    : {}),
                                ...(hover
                                    ? {
                                        InputProps: {
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    {fieldsGrid.units?.[textFieldProps.name] && (
                                                        <Typography component="span" color="text.secondary" sx={{mr: 0.75, fontSize: 14}}>
                                                            {fieldsGrid.units[textFieldProps.name]}
                                                        </Typography>
                                                    )}
                                                    <Tooltip
                                                        title={hover}
                                                        arrow
                                                        placement="top"
                                                        componentsProps={HOVER_BOX}
                                                    >
                                                        <InfoOutlinedIcon
                                                            fontSize="small"
                                                            sx={{color: '#0A8572', cursor: 'help'}}
                                                            data-testid={`hover-${textFieldProps.name}`}
                                                        />
                                                    </Tooltip>
                                                </InputAdornment>
                                            )
                                        }
                                    }
                                    : {})
                            });

                            return (
                                <React.Fragment key={textFieldProps.name}>
                                    {fieldsGrid.headings?.[textFieldProps.name] && (
                                        <Typography sx={{gridColumn: '1 / -1', fontWeight: 600, mt: 1, ...fieldsGrid.place?.[`heading-${textFieldProps.name}`]}}>
                                            {fieldsGrid.headings[textFieldProps.name]}
                                        </Typography>
                                    )}
                                    {/* fieldsGrid.place: any other grid position for a field or a heading */}
                                    <Box sx={{gridColumn: fieldsGrid.span?.[textFieldProps.name], minWidth: 0, ...fieldsGrid.place?.[textFieldProps.name]}}>
                                        {field}
                                    </Box>
                                </React.Fragment>
                            );
                        })}
                        {/* fieldsGrid.inGrid: custom parts drawn inside the grid, placed by fieldsGrid.place */}
                        {(custom ?? []).filter(option => fieldsGrid.inGrid?.includes(option.name)).map(option => (
                            <Box key={option.name} sx={{minWidth: 0, ...fieldsGrid.place?.[option.name]}}>
                                {option.render({
                                    onChange: data => setCustomFields(prev => ({...prev, [option.name]: data})),
                                    value: customFields[option.name]
                                })}
                            </Box>
                        ))}
                    </Box>
                )
                : Object.values(fields).map(({textFieldProps}) =>
                    fieldComponents[textFieldProps.inputType ?? 'text'](textFieldProps))}
            {
                options
                    ? <FormGroup>
                        {
                            options.map(({label, type = 'checkbox'}, index) => (
                                <FormControlLabel
                                    key={index}
                                    disabled={isSubmitted}
                                    control={<OptionComponent
                                        type={type}
                                        checked={selectedOptions.includes(index)}
                                        onChange={(e) => handleSelectOption(e, index)}
                                    />}
                                    label={label}
                                />
                            ))
                        }
                    </FormGroup>
                    : ''
            }
            {
                custom
                    ? custom.filter(option => !fieldsGrid?.inGrid?.includes(option.name)).map((option) => option.render({
                        onChange: data => setCustomFields(prev => ({...prev, [option.name]: data})),
                        value: customFields[option.name]
                    }))
                    : ''
            }
            {
                errorCaption
                    ? <Typography variant="caption" color="red">{errorCaption}</Typography>
                    : ''
            }
            <Stack
                flexDirection="row"
                justifyContent="flex-start"
                {...actionsContainerProps?.container}
            >
                <Button
                    sx={{
                        my: 1,
                        backgroundColor: '#186BBE',
                        boxShadow: 'none',
                        '&:hover': {
                            backgroundColor: '#155FA7',
                            boxShadow: 'none'
                        }
                    }}
                    variant="contained"
                    color="primary"
                    type="submit"
                    fullWidth={buttonFullWidth}
                    disabled={isSubmitted || !isFormValid || isButtonDisabled}
                    {...actionsContainerProps?.button}
                >
                    {buttonText}
                </Button>
            </Stack>
        </form>
    );
};

AdmicityForm.defaultProps = {
    errorCaption: '',
    handleChange: () => {
    },
    buttonFullWidth: true
};

AdmicityForm.propTypes = {
    title: PropTypes.string,
    textFields: PropTypes.array,
    dateFields: PropTypes.array,
    options: PropTypes.array,
    custom: PropTypes.array,
    handleSubmit: PropTypes.func,
    handleChange: PropTypes.func,
    setIsChildFormValid: PropTypes.func,
    errorCaption: PropTypes.string,
    buttonText: PropTypes.string,
    buttonFullWidth: PropTypes.bool,
    isButtonDisabled: PropTypes.bool,
    actionsContainerProps: PropTypes.object,
    showWarningMessage: PropTypes.bool,
    fieldsGrid: PropTypes.object,
};

export default React.forwardRef(AdmicityForm);